import axios from '@/api/axios';
import {toQueryString} from '@/api/queryString';
import type {
  AdminAfriexPerson,
  AdminPaginatedEnvelope,
  AfriexAdminTransaction,
  AfriexRatePromo,
  UserType,
} from '@/types';
import {CountryStatus} from '@/types';
import type {AfriexQueryParams} from '@/types/AfriexQueryParams';

export const tierService = {
  getTierList: async (
    filters: AfriexQueryParams & {target: UserType},
  ): Promise<AdminPaginatedEnvelope<AfriexRatePromo[]>> => {
    const {status, ...rest} = filters;
    const transformedFilters = {
      ...rest,
      ...(status && {isActive: status === CountryStatus.ACTIVE}),
    };

    const params = {page: 0, limit: 10, ...transformedFilters};
    const url = `/v2/admin/promos?${toQueryString(params)}`;
    const response =
      await axios.get<AdminPaginatedEnvelope<AfriexRatePromo[]>>(url);
    return response.data;
  },
  getTierUserList: async (
    tierId: string,
    filters: AfriexQueryParams,
  ): Promise<AdminPaginatedEnvelope<Partial<AdminAfriexPerson>[]>> => {
    const params = {page: 0, limit: 10, ...filters};
    const url = `/v2/admin/tiers/users/${tierId}?${toQueryString(params)}`;
    const response =
      await axios.get<AdminPaginatedEnvelope<Partial<AdminAfriexPerson>[]>>(
        url,
      );
    return response?.data;
  },
  getTierTransactionList: async (
    tierId: string,
    filters: AfriexQueryParams,
  ): Promise<AdminPaginatedEnvelope<Partial<AfriexAdminTransaction>[]>> => {
    const params = {page: 0, limit: 10, ...filters};
    const url = `/v2/admin/tiers/transactions/${tierId}?${toQueryString(
      params,
    )}`;
    const response =
      await axios.get<
        AdminPaginatedEnvelope<Partial<AfriexAdminTransaction>[]>
      >(url);
    return response?.data;
  },
  getTier: async (tierId: string): Promise<AfriexRatePromo> => {
    const url = `/v2/admin/promos/${tierId}`;
    const response = await axios.get<AfriexRatePromo>(url);
    return response?.data;
  },
  updateTier: async (
    tierId: string,
    tier: Partial<AfriexRatePromo>,
    target: UserType = 'AFRIEX',
  ): Promise<AfriexRatePromo> => {
    const url = `/v2/admin/promos/${tierId}`;
    // Spread `tier` last so a promo's own target (e.g. BUSINESS) is preserved;
    // `target` is only a fallback when the tier has none. Mirrors createTier.
    const response = await axios.patch<AfriexRatePromo>(url, {
      target,
      ...tier,
    });
    return response?.data;
  },
  createTier: async (
    tier: Partial<AfriexRatePromo>,
    target: UserType = 'AFRIEX',
  ): Promise<AfriexRatePromo> => {
    const updatedTier = {
      target,
      ...tier,
    } as AfriexRatePromo;

    const url = `/v2/admin/promos`;
    const response = await axios.post<AfriexRatePromo>(url, updatedTier);
    return response?.data;
  },
};
